// Canvas-safe image loading.
//
// Drawing a cross-origin <img> on a canvas "taints" it and toDataURL() throws —
// this is what breaks crop/markup inside the native app (capacitor://localhost)
// or when media lives on external storage behind a 307 redirect without CORS.
//
// Strategy: fetch the bytes ourselves (our backend answers with CORS *;
// `?proxy=1` makes it stream external URLs server-side), turn them into a
// same-origin blob URL and decode that — the canvas never gets tainted.

function decodeImage(src, useCors = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e || new Error("image decode failed"));
    img.src = src;
  });
}

// Ensures API media URLs are proxied server-side (covers external-storage media)
export function canvasSafeSrc(src) {
  if (!src) return src;
  if (/\/media\/[^/]+\/raw$/.test(src)) return `${src}?proxy=1`;
  return src;
}

export async function loadCanvasImage(src) {
  const url = canvasSafeSrc(src);
  // data: URLs are already same-origin — decode directly
  if (url.startsWith("data:") || url.startsWith("blob:")) return decodeImage(url);
  // 1) blob route — never taints the canvas
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`http ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await decodeImage(objectUrl);
    } finally {
      // safe to revoke after decode — pixels are already in memory
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    }
  } catch (e) {
    console.warn("loadCanvasImage: blob route failed, falling back to CORS <img>", e);
  }
  // 2) fallback: direct load with CORS attribute (old behavior)
  return decodeImage(url, true);
}
